"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

const subscribe = () => () => {};
const getClientSnapshot = () => true;
const getServerSnapshot = () => false;

/**
 * 하단 고정 요소를 화면 위에서 시작하는 공통 영역 안에 배치한다.
 * iOS의 bottom:0 기준이 주소창/키보드 전환 뒤 어긋나는 경우에 대비해
 * 독과 패널이 같은 visual viewport 높이를 사용한다(JCG-4343).
 */
export function MobileBottomToolViewport({
  children,
  mobileOnly,
}: React.PropsWithChildren<{ mobileOnly: boolean }>) {
  const mounted = React.useSyncExternalStore(
    subscribe,
    getClientSnapshot,
    getServerSnapshot,
  );
  const ref = React.useRef<HTMLDivElement>(null);

  React.useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const viewport = window.visualViewport;
    let frame = 0;
    let keyboardOpen = false;

    const update = () => {
      frame = 0;
      // 핀치 줌 중에는 독을 확대된 화면 안으로 강제로 따라오게 하지 않는다.
      const trackViewport = viewport && viewport.scale === 1;
      const active = document.activeElement;
      const isTextInput =
        active instanceof HTMLTextAreaElement
          ? !active.readOnly && !active.disabled && active.inputMode !== "none"
          : active instanceof HTMLInputElement
            ? !active.readOnly && !active.disabled && active.inputMode !== "none" &&
              ["text", "search", "email", "url", "tel", "password", "number"].includes(active.type)
            : active instanceof HTMLElement && active.isContentEditable;
      // 키보드 이벤트가 없는 Safari에서는 입력 포커스 + 큰 화면 축소로 판정한다.
      // 주소창 변화/외장 키보드의 포커스만으로는 숨기지 않는다. blur 뒤에도
      // 키보드 닫힘 애니메이션이 끝나 화면 높이가 복구될 때까지 숨김을 유지한다.
      const keyboardSizedGap = trackViewport &&
        window.innerHeight - viewport.height > Math.max(150, window.innerHeight * 0.2);
      keyboardOpen = Boolean(keyboardSizedGap && (isTextInput || keyboardOpen));
      node.toggleAttribute("data-keyboard-open", keyboardOpen);
      if (keyboardOpen) {
        node.style.setProperty("--mobile-tool-dock-visibility", "hidden");
        node.style.setProperty("--mobile-tool-dock-offset", "0px");
      } else {
        node.style.removeProperty("--mobile-tool-dock-visibility");
        node.style.removeProperty("--mobile-tool-dock-offset");
      }
      node.style.top = `${trackViewport ? viewport.offsetTop : 0}px`;
      node.style.setProperty(
        "--mobile-tool-viewport-height",
        trackViewport ? `${viewport.height}px` : "100dvh",
      );
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    update();
    document.addEventListener("focusin", schedule);
    document.addEventListener("focusout", schedule);
    window.addEventListener("resize", schedule);
    window.addEventListener("pageshow", schedule);
    viewport?.addEventListener("resize", schedule);
    viewport?.addEventListener("scroll", schedule);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("focusin", schedule);
      document.removeEventListener("focusout", schedule);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("pageshow", schedule);
      viewport?.removeEventListener("resize", schedule);
      viewport?.removeEventListener("scroll", schedule);
    };
  }, [mounted]);

  if (!mounted) return null;

  return createPortal(
    <div
      ref={ref}
      data-mobile-tool-viewport
      className={cn(
        "pointer-events-none fixed inset-x-0 top-0 z-40 h-[var(--mobile-tool-viewport-height,100dvh)]",
        mobileOnly && "lg:hidden",
      )}
    >
      {children}
    </div>,
    document.body,
  );
}
